import { useEffect, useState, Fragment, type ReactNode } from "react";

import clsx from "clsx";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useToasts } from "react-toast-notifications";

import { useUser } from "@components/user-context";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { useNorthbeamMutation } from "@hooks/http";
import { prettyLogEvent } from "@internal/analytics";
import {
  type ExportedOrderData,
  type ExportedOrderDataVariables,
} from "@north-beam/nb-client-side";
import { Pages } from "@north-beam/nb-client-side/constants";
import { EXPORT_ORDER_DATA } from "@pages/settings/queries";
import { PrimaryButton, SecondaryButton } from "@shared/buttons";
import { Modal } from "@shared/modal";
import { TextInput } from "@shared/text-input";

import { useOrdersPageFilters } from "./hooks/use-orders-filters";

export const OrderTableExport = ({ className }: { className?: string }) => {
  const filterOptions = useOrdersPageFilters();

  const { user } = useUser();
  const { addToast } = useToasts();
  const [exportFormat, setExportFormat] = useState<"default" | "api" | null>(
    null,
  );
  const [fileName, setFileName] = useState("order_data");
  const [exportData, { loading }] = useNorthbeamMutation<
    ExportedOrderData,
    ExportedOrderDataVariables
  >(EXPORT_ORDER_DATA, { useCpuIntensive: true });

  useEffect(() => {
    setFileName(exportFormat === "api" ? "orders_api" : "order_data");
  }, [exportFormat]);

  return (
    <>
      <Modal
        isOpen={exportFormat !== null}
        onClose={() => setExportFormat(null)}
        titleContent={
          <div className="text-lg font-semibold">
            <i
              className="fa-regular fa-sharp fa-download mr-5"
              style={{ fontSize: "1.5em" }}
            />
            Export your Order Data
          </div>
        }
      >
        <div className="mt-4">
          Your order data export will be based on the filters that you have
          applied on the order page.
        </div>
        {exportFormat === "api" && (
          <div className="mt-4">
            Your export will be in the format of the Orders API CSV template.
          </div>
        )}
        <div className="mt-4">
          Your export may take up to 1 hour to complete depending on the size of
          the export.
        </div>
        <div className="mt-4">
          Exports can be found in the{" "}
          <Link to="../customers/documents" target="_blank" rel="noreferrer">
            Documents page.
          </Link>
        </div>
        <div className="mt-4">
          File name:
          <TextInput
            className="text-sm"
            value={fileName}
            onChange={(e) => {
              if (e.target.value.match(/^[A-Za-z0-9_\-]*$/)) {
                setFileName(e.target.value);
              }
            }}
          />
          {!fileName && (
            <div className="font-normal text-[0.65rem] text-mars mt-0.5">
              File Name is required
            </div>
          )}
          {fileName && fileName.length < 3 && (
            <div className="font-normal text-[0.65rem] text-mars mt-0.5">
              File Name must be at least 3 characters long
            </div>
          )}
        </div>
        <div className="text-right">
          <PrimaryButton
            className="mt-8"
            isDisabled={loading || !fileName || fileName.length < 3}
            loading={loading}
            size="md"
            onClick={async () => {
              if (!exportFormat) {
                return;
              }

              const name = `${fileName}-${new Date().getTime()}`;
              await exportData({
                variables: {
                  filterOptions,
                  name,
                  format: exportFormat,
                },
              });

              addToast(
                <div>
                  <div>Your order export has started!</div>
                  <div className="mt-3">
                    When your export is done it will live in the Documents page
                    under the name:
                  </div>
                  <div className="mt-3">{name}.csv</div>
                </div>,
                { appearance: "success", autoDismiss: false },
              );
              prettyLogEvent.onSubmit({
                page: Pages.ORDERS,
                additionalActionInfo: "order-export",
                data: { filterOptions, name, exportFormat },
                user,
              });
              setExportFormat(null);
            }}
          >
            EXPORT
          </PrimaryButton>
        </div>
      </Modal>
      {user.featureFlags.enableOrdersAdvancedExport ? (
        <div className="relative">
          <Menu>
            {({ open }) => (
              <>
                <MenuButton
                  as="button"
                  className={clsx("button secondary-button sm", {
                    "text-white bg-cosmic border-cosmic": open,
                  })}
                >
                  <span className="mr-2">EXPORT TABLE DATA</span>
                  <motion.i
                    initial={false}
                    animate={{ rotate: open ? 0 : 180 }}
                    transition={{ duration: 0.2 }}
                    className={clsx("fa-regular fa-sharp fa-chevron-up", {
                      "text-white": open,
                    })}
                  />
                </MenuButton>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-200"
                  enterFrom="opacity-0 translate-y-1"
                  enterTo="opacity-100 translate-y-0"
                  leave="transition ease-in duration-150"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-1"
                >
                  <MenuItems
                    className={clsx(
                      "absolute z-10 right-0 bg-white rounded-lg elevation-1",
                      "text-sm pt-1 pb-2 w-[180px]",
                    )}
                  >
                    <ExportMenuOption
                      onClick={() => setExportFormat("default")}
                    >
                      <i className="fa-regular fa-sharp fa-table text-primary mr-2" />
                      Default format
                    </ExportMenuOption>
                    <ExportMenuOption onClick={() => setExportFormat("api")}>
                      <div className="relative">
                        <i className="fa-regular fa-sharp fa-table text-primary mr-2" />
                        <span
                          className={clsx(
                            "absolute left-[4px] bottom-0 bg-cosmic text-white rounded",
                            "text-[7px] leading-[7px] py-[1px] px-[4px]",
                          )}
                        >
                          API
                        </span>
                      </div>
                      Orders API format
                    </ExportMenuOption>
                  </MenuItems>
                </Transition>
              </>
            )}
          </Menu>
        </div>
      ) : (
        <SecondaryButton
          className={className}
          isDisabled={loading}
          onClick={() => {
            setExportFormat("default");
          }}
        >
          EXPORT DATA
        </SecondaryButton>
      )}
    </>
  );
};

const ExportMenuOption = ({
  children,
  onClick,
}: {
  children: ReactNode;
  onClick(): void;
}) => (
  <MenuItem as={Fragment}>
    {({ focus }) => {
      return (
        <div
          className={clsx(
            "w-full text-left px-4 h-8 flex items-center cursor-pointer",
            {
              "bg-cosmic-4": focus,
            },
          )}
          onClick={onClick}
        >
          {children}
        </div>
      );
    }}
  </MenuItem>
);
